import axios from 'axios';
import configData from '../config.json';

// Customer access-token refresh (backend: POST /user/refreshtoken).
//
// Logins made after the backend change return a 24h `token` plus a
// non-expiring `refreshToken` that is ROTATED on every use — the old one is
// revoked the moment a new pair is issued. Both live in `user_profile`, which
// every login path already stores wholesale. Sessions from before the change
// have no refreshToken and a token without `exp`; they are left untouched.
//
// Dozens of call sites read the token into component state and build their own
// `Authorization` header, so rather than touching each one this installs two
// global axios interceptors:
//   - request:  a customer bearer token that is expired/about to expire is
//               refreshed first (the backend asks that a logged-in user refresh
//               before calling other endpoints), and a stale token held in
//               component state is swapped for the current stored one.
//   - response: a 401 on a customer-token request refreshes once and retries.
//
// Rotation makes concurrent refreshes destructive (the loser's refresh token is
// already revoked), so refreshes are single-flight within a tab and serialised
// across tabs with the Web Locks API where available.

const STORAGE_KEY = 'user_profile';
const LOCK_NAME = 'hw-user-token-refresh';
const EXPIRY_MARGIN_MS = 5 * 60 * 1000;
const RETRIED_FLAG = '_hwAuthRetried';
// Only used when a refresh 401s and another tab may still be mid-rotation.
const ROTATION_WAIT_TRIES = 8;
const ROTATION_WAIT_STEP_MS = 250;
// Fired on `window` when the backend has rejected the stored session and it had
// to be dropped — never on a deliberate sign-out. components/SessionExpiryWatcher
// turns it into the UI half (clear the profile, explain, offer the login modal).
export const SESSION_EXPIRED_EVENT = 'hw:session-expired';

// Separate instance so the auth calls never re-enter these interceptors.
const authClient = axios.create();

let inflightRefresh = null;
let inflightStatus = null;
let startupCheck = null;
// Server clock minus client clock, learned from the `iat` of freshly issued
// tokens, so a device with a wrong clock doesn't refresh on every request.
let clockSkewMs = 0;

export const readStoredProfile = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return null;
  }
};

export const decodeJwtPayload = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
};

const isCustomerToken = (token) => decodeJwtPayload(token)?.type === 'User';

const isSameUser = (a, b) => {
  const idA = decodeJwtPayload(a)?.userid;
  return !!idA && idA === decodeJwtPayload(b)?.userid;
};

export const isTokenExpiring = (token) => {
  const exp = decodeJwtPayload(token)?.exp;
  if (!exp) return false; // pre-refresh-token sessions never expire
  return exp * 1000 - (Date.now() + clockSkewMs) < EXPIRY_MARGIN_MS;
};

const learnClockSkew = (token) => {
  const iat = decodeJwtPayload(token)?.iat;
  if (iat) clockSkewMs = iat * 1000 - Date.now();
};

const withCrossTabLock = (fn) => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(LOCK_NAME, fn);
  }
  return fn();
};

// Polls storage briefly for another tab replacing `refreshToken` with a rotated
// one. Only ever runs on the 401 path, where the alternative is a lost session.
const awaitRotationBy = async (refreshToken) => {
  for (let attempt = 0; attempt < ROTATION_WAIT_TRIES; attempt += 1) {
    const latest = readStoredProfile();
    if (!latest || latest.refreshToken !== refreshToken) return latest;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, ROTATION_WAIT_STEP_MS));
  }
  return readStoredProfile();
};

// Drops a session the backend has rejected and tells the app once. Deliberate
// sign-out goes through signOutSession()/the pages' own logOut instead.
const endSession = () => {
  if (!localStorage.getItem(STORAGE_KEY)) return;
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
};

const requestTokenStatus = async (token) => {
  try {
    const { data } = await authClient.get(`${configData.SERVER_URL}/user/token/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data;
  } catch (error) {
    // The verdict rides along with the 401: { supported: false, retired: … }.
    // Any other status — 5xx, 400, offline — says nothing about the token, and
    // the caller only acts on an explicit supported: false.
    return error?.response?.status === 401 ? error.response.data : null;
  }
};

// GET /user/token/status — the backend's direct answer to "would you still
// accept this access token?", for exactly the tokens it would otherwise reject
// on some unrelated request (expired, retired, malformed). Single-flighted so a
// burst of 401s asks once.
const checkTokenStatus = (token) => {
  if (inflightStatus?.token !== token) {
    const promise = requestTokenStatus(token).finally(() => {
      if (inflightStatus?.promise === promise) inflightStatus = null;
    });
    inflightStatus = { token, promise };
  }
  return inflightStatus.promise;
};

// Ends the session only when the backend positively says the token is no longer
// supported. Returns true while the token is still usable — or when the answer
// could not be obtained, so a blip never signs anyone out.
const verifyOrEndSession = async (token) => {
  const status = await checkTokenStatus(token);
  if (status?.supported !== false) return true;
  // Another tab may have signed in meanwhile; only end the session we checked.
  if (readStoredProfile()?.token !== token) return true;
  endSession();
  return false;
};

// Resolves to a usable access token, or null if the session can't be refreshed.
// `staleToken` is the token the caller already knows to be unusable.
const doRefresh = (staleToken) =>
  withCrossTabLock(async () => {
    const stored = readStoredProfile();
    if (!stored?.refreshToken) return null;

    // Another tab refreshed while we waited for the lock.
    if (stored.token && stored.token !== staleToken && !isTokenExpiring(stored.token)) {
      return stored.token;
    }

    let data;
    try {
      ({ data } = await authClient.post(`${configData.SERVER_URL}/user/refreshtoken`, {
        refreshToken: stored.refreshToken,
      }));
    } catch (error) {
      if (error?.response?.status !== 401) return null; // network/5xx: keep the session
      // A 401 can also mean we lost a rotation race to a tab that refreshed
      // without the lock (no Web Locks support). That tab may not have written
      // its new pair yet, so give it a moment before writing the session off.
      const latest = await awaitRotationBy(stored.refreshToken);
      if (latest?.refreshToken && latest.refreshToken !== stored.refreshToken) {
        return latest.token || null;
      }
      // The refresh token really is revoked; drop the dead session so the next
      // load starts as a guest instead of failing every request.
      if (latest?.refreshToken === stored.refreshToken) {
        endSession();
      }
      return null;
    }

    if (!data?.token || !data?.refreshToken) return null;
    learnClockSkew(data.token);

    // Logged out or switched accounts while the request was in flight.
    const latest = readStoredProfile();
    if (latest?.refreshToken !== stored.refreshToken) return null;

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...latest, token: data.token, refreshToken: data.refreshToken }),
    );
    return data.token;
  });

export const refreshAccessToken = (staleToken) => {
  if (!inflightRefresh) {
    inflightRefresh = doRefresh(staleToken).finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
};

// Rotate once on app open, as the backend asks, whether or not the current
// access token has expired: it keeps an unused-but-leaked refresh token from
// staying valid indefinitely. Customer-token requests made while this is in
// flight wait for it, so nothing goes out on a token that is about to rotate.
// A second tab opening at the same time does NOT rotate again — it picks up the
// pair the first one just stored (see doRefresh), rather than racing it.
// A session from before refresh tokens has nothing to rotate, so it asks
// /user/token/status instead: the backend can retire those older tokens, and
// without this the user would keep looking signed in while every request failed.
export const refreshOnAppOpen = () => {
  const stored = readStoredProfile();
  if (!stored?.token) return Promise.resolve(null);
  const check = stored.refreshToken
    ? refreshAccessToken(stored.token)
    : verifyOrEndSession(stored.token).then(() => null);
  startupCheck = check.finally(() => {
    startupCheck = null;
  });
  return startupCheck;
};

// For code that writes an in-memory copy of the profile back to storage: keep
// the current token pair, or a rotated refresh token would be overwritten by
// the revoked one it replaced.
export const withLatestTokens = (profile) => {
  const stored = readStoredProfile();
  if (!stored?.refreshToken) return profile;
  return { ...profile, token: stored.token, refreshToken: stored.refreshToken };
};

// A token handed over out-of-band (the mobile app's `/profile?token=` deep link)
// arrives on its own. If it belongs to the same user as the stored session,
// carry that session's refresh token over so the rehydrated session can still
// rotate instead of dying when the 24h token runs out. For anyone else's token
// this returns undefined, and the session stays non-refreshable.
export const carriedRefreshToken = (token) => {
  const stored = readStoredProfile();
  if (!stored?.refreshToken || !isSameUser(token, stored.token)) return undefined;
  return stored.refreshToken;
};

// Revokes the refresh token server-side. Call BEFORE clearing `user_profile`.
// keepalive lets it finish even if the caller navigates away immediately.
export const signOutSession = () => {
  const stored = readStoredProfile();
  if (!stored?.token && !stored?.refreshToken) return;
  try {
    fetch(`${configData.SERVER_URL}/user/signout`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${stored.token || ''}`,
      },
      body: JSON.stringify({ refreshToken: stored.refreshToken }),
    }).catch(() => {});
  } catch (e) {
    // Best effort — logout proceeds locally regardless.
  }
};

const getAuthHeaderKey = (headers) =>
  Object.keys(headers || {}).find((key) => key.toLowerCase() === 'authorization');

const getBearer = (headers) => {
  const key = getAuthHeaderKey(headers);
  const value = key && headers[key];
  return typeof value === 'string' && value.startsWith('Bearer ') ? value.slice(7) : null;
};

const isApiRequest = (config) =>
  typeof config?.url === 'string' && config.url.startsWith(configData.SERVER_URL);

// The customer token this request should use, if it carries one we manage.
const managedToken = (config) => {
  if (!isApiRequest(config)) return null;
  const sent = getBearer(config.headers);
  if (!sent || !isCustomerToken(sent)) return null;
  const stored = readStoredProfile();
  // Only manage sessions that can refresh, and never swap in a different
  // user's token (e.g. the /profile?token= deep link validating a new one).
  if (!stored?.refreshToken || !isSameUser(sent, stored.token)) return null;
  return { sent, stored };
};

const setBearer = (config, token) => {
  const key = getAuthHeaderKey(config.headers) || 'Authorization';
  config.headers = { ...config.headers, [key]: `Bearer ${token}` };
  return config;
};

export const installAuthInterceptors = (instance = axios) => {
  const requestId = instance.interceptors.request.use(async (config) => {
    // Hold authenticated calls until the app-open rotation lands; public
    // requests (menus, restaurants) are never delayed by it.
    if (startupCheck && isApiRequest(config) && isCustomerToken(getBearer(config.headers))) {
      await startupCheck;
    }
    const managed = managedToken(config);
    if (!managed) return config;
    let token = managed.stored.token;
    if (isTokenExpiring(token)) {
      token = (await refreshAccessToken(token)) || token;
    }
    return token !== managed.sent ? setBearer(config, token) : config;
  });

  const responseId = instance.interceptors.response.use(undefined, async (error) => {
    const config = error?.config;
    if (error?.response?.status !== 401 || !config || config[RETRIED_FLAG]) {
      throw error;
    }
    const managed = managedToken(config);
    if (!managed) {
      // A customer token with no way to refresh (a pre-refresh-token session).
      // One 401 is not proof on its own, so let the backend confirm the token is
      // retired before ending the session. Matching the stored token is what
      // makes it ours: a staff or other-user token can never be equal to it.
      const sent = getBearer(config.headers);
      if (isApiRequest(config) && sent && sent === readStoredProfile()?.token) {
        await verifyOrEndSession(sent);
      }
      throw error;
    }
    const token = await refreshAccessToken(managed.sent);
    if (!token) throw error;
    return instance(setBearer({ ...config, [RETRIED_FLAG]: true }, token));
  });

  return () => {
    instance.interceptors.request.eject(requestId);
    instance.interceptors.response.eject(responseId);
  };
};
